import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuthenticatedApi } from 'src/services/auth.service';
import { getLogger } from 'src/utils/logger';
import { GenerationResult } from 'src/models/form-types';
import { Strategy } from 'src/services/strategies.service';
import { GENERIC_ERROR_MESSAGE } from 'src/utils/errorMessages';

const logger = getLogger('useStrategiesGeneration');

export function useStrategiesGeneration(
  generateService: (data: { smartGoal: string, numStrategies: number }, token: string) => Promise<any>,
  smartGoal: string | null
) {
  const [generationState, setGenerationState] = useState<GenerationResult<Strategy[]>>({
    isLoading: false,
    error: null,
    data: null
  });
  
  const [selectedStrategies, setSelectedStrategies] = useState<string[]>([]);
  const { isAuthenticated } = useAuth0();
  const { callApi } = useAuthenticatedApi();

  const generateStrategies = useCallback(async () => {
    if (!smartGoal) {
      setGenerationState({
        isLoading: false,
        error: 'No SMART goal selected. Please select a SMART goal first.',
        data: null
      });
      return;
    }

    // Default number of strategies to generate
    const numStrategies = 6;

    setGenerationState(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));
    
    try {
      if (isAuthenticated) {
        // Create a clean data object to avoid circular references
        const requestData = {
          smartGoal: String(smartGoal),
          numStrategies: Number(numStrategies)
        };
        
        const { data, error } = await callApi(generateService, requestData);
        
        if (error) {
          logger.error('Error generating strategies:', error);
          setGenerationState({
            isLoading: false,
            error: error.message || 'An error occurred while generating strategies',
            data: null
          });
        } else if (data?.strategies) {
          // Avoid circular references in debug logging
          logger.debug(`Strategies generated: ${data.strategies.length}`);
          
          setGenerationState({
            isLoading: false,
            error: null,
            data: data.strategies
          });
          
          // Reset selected strategies when new ones are generated
          setSelectedStrategies([]);
        } else {
          setGenerationState({
            isLoading: false,
            error: 'No strategies returned from the service',
            data: null
          });
        }
      } else {
        setGenerationState({
          isLoading: false,
          error: 'Authentication required to generate strategies',
          data: null
        });
      }
    } catch (error) {
      logger.error('Exception generating strategies:', error);
      setGenerationState({
        isLoading: false, 
        error: GENERIC_ERROR_MESSAGE,
        data: null
      });
    }
  }, [isAuthenticated, callApi, generateService, smartGoal]);

  const toggleStrategySelection = useCallback((strategyText: string) => {
    setSelectedStrategies(prev => {
      if (prev.includes(strategyText)) {
        return prev.filter(s => s !== strategyText);
      } else {
        return [...prev, strategyText];
      }
    });
  }, []);

  return {
    strategies: generationState.data || [],
    isLoading: generationState.isLoading,
    error: generationState.error,
    selectedStrategies,
    generateStrategies,
    toggleStrategySelection
  };
}
